/**
 * M28 (2026-05-14) — Macro-Regime-Panel (FRED-API basiert).
 *
 * Zeigt die wichtigsten US-Makro-Indikatoren (Yield-Kurve, High-Yield-Spread,
 * Inflation, Leitzins) + ein berechnetes Gesamt-Regime (neutral/warning/danger)
 * mit Klartext-Signalen.
 *
 * Datenquelle: v5_macro_regime (taeglicher Cron 07:00 UTC).
 */

import { useDataState } from '@/hooks/use-data-state';
import { supabase } from '@/lib/supabase';
import { normalizeMacroRegime } from '@/lib/macro-regime';
import { DataStateView } from '@/components/ui/data-state-view';
import { ShieldAlert, ShieldCheck, AlertTriangle, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CardInfoIcon } from '@/components/ui/CardInfoIcon';
import { useCallback } from 'react';
import { CARD, CARD_HEADER } from '@/lib/design-tokens';
// W36-U3 — Klartext-Tooltips für T10Y2Y / HY-Spread / Fed-Funds / CPI / Unemployment
import { TermTooltip } from '@/components/ui/term-tooltip';

export interface MacroRegimeRow {
  observation_date: string;
  t10y2y_spread: number | null;
  hy_spread: number | null;
  fed_funds_rate: number | null;
  cpi_yoy: number | null;
  unemployment_rate: number | null;
  // Welle 14: kanonische Score-Reader-Vokabel (der FRED-Cron schreibt diese
  // Werte, NICHT mehr neutral/warning/danger). Muss zu macro-regime.ts passen.
  regime: 'risk_on' | 'neutral' | 'risk_off' | 'crisis';
  regime_score: number;
  signals: string[];
  fetched_at: string;
}

export async function fetchLatestMacroRegime(): Promise<MacroRegimeRow | null> {
  const { data, error } = await supabase
    .from('v5_macro_regime')
    .select('*')
    .order('observation_date', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  return (data?.[0] as MacroRegimeRow | undefined) ?? null;
}

// Welle 14 — 4 kanonische Regime-Werte (risk_on|neutral|risk_off|crisis), die
// der FRED-Cron + der Score-Reader (macro-regime.ts) verwenden. Score: 100 =
// max Risk-On/ruhig, 0 = Krise.
const REGIME_STYLE = {
  risk_on: {
    label: 'Risk-On',
    icon: <ShieldCheck className="size-4" />,
    className: 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200',
    description: 'Makro-Umfeld ruhig (enge Spreads, gesunde Kurve). Risk-On-Strategien bekommen leichten Rückenwind.',
  },
  neutral: {
    label: 'Neutral',
    icon: <ShieldCheck className="size-4" />,
    className: 'border-zinc-700/60 bg-zinc-900/30 text-zinc-300',
    description: 'Makro-Indikatoren im Normbereich. Kein Auf- oder Abschlag aufs Scoring.',
  },
  risk_off: {
    label: 'Risk-Off',
    icon: <AlertTriangle className="size-4" />,
    className: 'border-amber-700/60 bg-amber-950/30 text-amber-200',
    description: 'Ein wichtiger Frühwarn-Indikator zeigt Stress. Risk-On-Strategien werden gedämpft, Position-Sizing konservativ.',
  },
  crisis: {
    label: 'Krise',
    icon: <ShieldAlert className="size-4" />,
    className: 'border-red-700/60 bg-red-950/30 text-red-200',
    description: 'Mehrere Indikatoren signalisieren Risiko (Kredit-Stress / tiefe Inversion). Risk-On-Strategien stark gedämpft, defensiv bleiben.',
  },
};

export function MacroRegimePanel() {
  const queryFn = useCallback(() => fetchLatestMacroRegime(), []);
  const state = useDataState({
    queryKey: ['macro-regime', 'latest'],
    queryFn,
    ttlSeconds: 60 * 60,
  });

  return (
    <section className={CARD}>
      <header className="flex items-center gap-2 mb-2">
        <Globe className="size-4 text-cockpit-accent" />
        <h2 className={CARD_HEADER}>
          US-Makro-Regime
          <CardInfoIcon
            hint={
              <>
                Frühwarn-Indikatoren der US-Wirtschaft. <strong>Yield-Kurve</strong> (Differenz
                zwischen langen und kurzen Anleihezinsen, invertiert = Rezessions-Signal),{' '}
                <strong>High-Yield-Spread</strong> (Risikoprämie für Junk-Bonds, hoch = Stress),
                Inflation, Leitzins. Wenn mehrere Indikatoren rot sind, wird typischerweise
                vorsichtiger gehandelt.
              </>
            }
          />
        </h2>
        <span className="text-[10px] text-zinc-500 ml-auto">FRED · tgl. 07:00 UTC</span>
      </header>
      <DataStateView
        state={state.state}
        empty={
          <div className="rounded-md border border-zinc-800/60 bg-zinc-950/30 p-3 text-[11px] text-zinc-400 leading-snug">
            Noch keine Makro-Daten. Der Cron läuft täglich 07:00 UTC und schreibt in
            v5_macro_regime. Falls dauerhaft leer:{' '}
            <strong className="text-zinc-200">FRED_API_KEY</strong> in
            <code className="mx-1 text-zinc-300">/etc/swingtrader/api.env</code> setzen —
            kostenlos unter{' '}
            <a
              className="underline text-cockpit-accent"
              href="https://fred.stlouisfed.org/docs/api/api_key.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              fred.stlouisfed.org/docs/api/api_key.html
            </a>
            .
          </div>
        }
      >
        {(regime) => {
          if (!regime) return null;
          // Welle 14 (P0-Fix): /opt-Cron kann noch 'warning'/'danger' liefern →
          // auf kanonische Vokabel normalisieren, sonst REGIME_STYLE[]=undefined.
          const style = REGIME_STYLE[normalizeMacroRegime(regime.regime)];
          return (
            <div className="space-y-3">
              {/* Regime-Banner */}
              <div className={cn('rounded-md border p-3 flex items-start gap-2', style.className)}>
                <span className="shrink-0 mt-0.5">{style.icon}</span>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-[13px]">{style.label}</span>
                    <span className="text-[10px] opacity-70">
                      Score {regime.regime_score}/100 · Stand {regime.observation_date}
                    </span>
                  </div>
                  <p className="text-[11px] mt-0.5 opacity-90">{style.description}</p>
                </div>
              </div>

              {/* Werte-Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <MacroValue
                  label="Yield-Kurve"
                  termKey="t10y2y"
                  value={regime.t10y2y_spread}
                  unit="%"
                  hint="10Y-2Y Treasury"
                  warn={regime.t10y2y_spread !== null && regime.t10y2y_spread < 0.3}
                  danger={regime.t10y2y_spread !== null && regime.t10y2y_spread < 0}
                  good={regime.t10y2y_spread !== null && regime.t10y2y_spread >= 1.0}
                />
                <MacroValue
                  label="HY-Spread"
                  termKey="hy-spread"
                  value={regime.hy_spread}
                  unit="%"
                  hint="High-Yield Bond-Risikoprämie"
                  warn={regime.hy_spread !== null && regime.hy_spread > 4.0}
                  danger={regime.hy_spread !== null && regime.hy_spread > 5.0}
                  good={regime.hy_spread !== null && regime.hy_spread < 3.0}
                />
                <MacroValue
                  label="Leitzins"
                  termKey="fed-funds-rate"
                  value={regime.fed_funds_rate}
                  unit="%"
                  hint="FED Funds Rate"
                  warn={regime.fed_funds_rate !== null && regime.fed_funds_rate > 5.0}
                />
                <MacroValue
                  label="Inflation"
                  termKey="cpi-yoy"
                  value={regime.cpi_yoy}
                  unit="%"
                  hint="CPI YoY (US-Verbraucherpreise)"
                  warn={regime.cpi_yoy !== null && regime.cpi_yoy > 4.0}
                  good={regime.cpi_yoy !== null && regime.cpi_yoy <= 2.5}
                />
                <MacroValue
                  label="Arbeitslos"
                  termKey="unemployment"
                  value={regime.unemployment_rate}
                  unit="%"
                  hint="US-Unemployment Rate"
                  warn={regime.unemployment_rate !== null && regime.unemployment_rate > 5.5}
                  good={regime.unemployment_rate !== null && regime.unemployment_rate < 4.5}
                />
              </div>

              {/* Signals als Bullets */}
              {regime.signals.length > 0 && (
                <ul className="space-y-1">
                  {regime.signals.map((s, i) => (
                    <li key={i} className="text-[11px] text-zinc-300 leading-snug pl-3 border-l-2 border-zinc-700">
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }}
      </DataStateView>
    </section>
  );
}

function MacroValue({
  label,
  termKey,
  value,
  unit,
  hint,
  warn = false,
  danger = false,
  good = false,
}: {
  label: string;
  /** W36-U3 — TermTooltip-Key fuer ausfuehrliche Erklaerung */
  termKey?: string;
  value: number | null;
  unit?: string;
  hint?: string;
  warn?: boolean;
  danger?: boolean;
  /** Audit 15.06. — grün = gesunder/günstiger Wert. Damit gut/schlecht sichtbar
      ist, nicht nur „schlecht vs. neutral-grau". */
  good?: boolean;
}) {
  const valueClass = danger
    ? 'text-red-300'
    : warn
      ? 'text-amber-300'
      : good
        ? 'text-emerald-300'
        : 'text-zinc-200';
  return (
    <div className="rounded-md border border-zinc-800/60 bg-zinc-950/30 p-2" title={hint}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        {termKey ? (
          <TermTooltip term={termKey} inline>
            {label}
          </TermTooltip>
        ) : (
          label
        )}
      </div>
      <div className={cn('text-base font-mono', valueClass)}>
        {value !== null ? `${value > 0 ? '+' : ''}${value.toFixed(2)}${unit ?? ''}` : '–'}
      </div>
    </div>
  );
}
